import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatGridList, MatGridTile } from '@angular/material/grid-list';
import type { Feature } from 'geojson';
import { getVlmBlock, type IVlmBlock } from './vlm-layer-panel.types';

interface VlmFeatureEntry {
  feature: Feature;
  vlm: IVlmBlock;
}

/**
 * Displays a card grid of VLM analysis results. Accepts a list of GeoJSON features and
 * an optional URL authentication function. The authUrlFn input defaults to the identity
 * function so the component is usable outside of an authenticated context.
 */
@Component({
  selector: 'lib-vlm-feature-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconButton, MatIcon, MatGridList, MatGridTile],
  templateUrl: './vlm-feature-list.component.html',
  styleUrl: './vlm-feature-list.component.scss',
})
export class VlmFeatureListComponent {
  readonly features = input.required<Feature[]>();
  readonly authUrlFn = input<(url: string) => string>((url: string) => url);
  readonly navigateTo = output<Feature>();

  protected get entries(): VlmFeatureEntry[] {
    return this.features()
      .map((f) => ({ feature: f, vlm: getVlmBlock(f)! }))
      .filter((e) => e.vlm !== null);
  }

  protected previewUrl(vlm: IVlmBlock): string | null {
    const raw = vlm.image_url ?? vlm.preview_url ?? null;
    return raw ? this.authUrlFn()(raw) : null;
  }

  protected scoreClass(score: number | undefined): string {
    if (score === undefined || score === null) return 'score-none';
    if (score >= 70) return 'score-high';
    if (score >= 40) return 'score-medium';
    return 'score-low';
  }

  protected propertyEntries(vlm: IVlmBlock): { key: string; value: string }[] {
    if (!vlm.vlm_properties) return [];
    return Object.entries(vlm.vlm_properties).map(([key, value]) => ({
      key,
      value: value === null || value === undefined ? '—' : String(value),
    }));
  }

  protected onNavigate(feature: Feature): void {
    this.navigateTo.emit(feature);
  }
}
